import * as Api from '@ViewModels';
import { type QueryClient, useQueries, useQuery, useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

export const CONTACT_QUERY_KEY = 'contact';

/**
 * Invalidates every cached `useContact` query for a contact, regardless of the `allowArchived` /
 * `expandHouseholdMembers` variant (TanStack matches the partial key). Keep contact query-key
 * management here so callers don't pin variants that drift out of sync with the query definitions.
 */
export const invalidateContactQueries = (queryClient: QueryClient, contactId: string) =>
	queryClient.invalidateQueries({ queryKey: [CONTACT_QUERY_KEY, { contactId }] });

const getQueryFn =
	({
		contactId,
		allowArchived = false,
		expandHouseholdMembers = false,
		userSession,
	}: {
		contactId: string;
		allowArchived?: boolean;
		expandHouseholdMembers?: boolean;
		userSession: Api.UserSessionContext;
	}) =>
	async () => {
		const expand = ['DuplicateContacts', 'TagsCollection'];

		if (expandHouseholdMembers) {
			expand.push('householdMembers');
		}

		return userSession.webServiceHelper.callAsync<Api.IContact>(
			Api.ImpersonationBroker.composeApiUrl({
				queryParams: {
					expand: expand.join(','),
					includeArchived: allowArchived,
				},
				urlPath: `contact/${contactId}`,
			}),
			'GET'
		);
	};

export const useContact = ({
	contactId,
	allowArchived = false,
	expandHouseholdMembers = false,
	refetchOnWindowFocus,
	enabled = true,
	retry,
}: {
	contactId: string;
	allowArchived?: boolean;
	expandHouseholdMembers?: boolean;
	refetchOnWindowFocus?: boolean;
	enabled?: boolean;
	retry?: boolean;
}) => {
	const userSession = useUserSession();

	return useQuery({
		enabled,
		queryFn: getQueryFn({ allowArchived, contactId, expandHouseholdMembers, userSession }),
		queryKey: [CONTACT_QUERY_KEY, { allowArchived, contactId, expandHouseholdMembers }],
		refetchOnWindowFocus,
		retry,
	});
};

export const useContactSuspenseQuery = ({
	contactId,
	allowArchived = false,
	expandHouseholdMembers = false,
}: {
	contactId: string;
	allowArchived?: boolean;
	expandHouseholdMembers?: boolean;
}) => {
	const userSession = useUserSession();
	return useSuspenseQuery({
		queryKey: [CONTACT_QUERY_KEY, { allowArchived, contactId, expandHouseholdMembers }],
		queryFn: getQueryFn({ allowArchived, contactId, expandHouseholdMembers, userSession }),
	});
};

export const useContactsQuery = ({
	contactIds,
	allowArchived = false,
	expandHouseholdMembers = false,
}: {
	contactIds: string[];
	allowArchived?: boolean;
	expandHouseholdMembers?: boolean;
}) => {
	const userSession = useUserSession();
	return useQueries({
		queries: contactIds.map(contactId => {
			return {
				queryFn: getQueryFn({ allowArchived, contactId, expandHouseholdMembers, userSession }),
				queryKey: [CONTACT_QUERY_KEY, { allowArchived, contactId, expandHouseholdMembers }],
			};
		}),
	});
};

export const useContactsSuspenseQuery = ({
	contactIds,
	allowArchived = false,
	expandHouseholdMembers = false,
}: {
	contactIds: string[];
	allowArchived?: boolean;
	expandHouseholdMembers?: boolean;
}) => {
	const userSession = useUserSession();
	return useSuspenseQueries({
		queries: contactIds.map(contactId => {
			return {
				queryKey: [CONTACT_QUERY_KEY, { allowArchived, contactId, expandHouseholdMembers }],
				queryFn: getQueryFn({ allowArchived, contactId, expandHouseholdMembers, userSession }),
			};
		}),
	});
};
export const buildContactQuery = ({
	contactId,
	allowArchived,
	expandHouseholdMembers,
}: {
	contactId: string;
	allowArchived: boolean;
	expandHouseholdMembers: boolean;
}) => {
	return [
		{
			queryKey: [CONTACT_QUERY_KEY, { allowArchived, contactId, expandHouseholdMembers }],
		},
	];
};
